import React, { useEffect, useMemo, useState } from "react";
import { useAuthContext } from "@asgardeo/auth-react";
import { Helmet } from "react-helmet-async";
import ReactMarkdown from "react-markdown";
import { useNavigate, useParams } from "react-router-dom";
import remarkBreaks from "remark-breaks";
import remarkGfm from "remark-gfm";
import SearchLayout from "../../layouts/SearchLayout";
import { getCaseByStandardCaseNumber } from "../../services/search-services";
import { encodeCaseNumberForPath, decodeCaseNumberFromPath, normalizeCaseNumbers } from "../../utils/caseNumber";

const PRESIGNED_BASE_URL = import.meta.env.VITE_PRESIGNED_BASE_URL;

const markdownComponents = {
  h1: ({ node, ...props }) => (
    <h1 className="mt-8 first:mt-0 text-xl font-display font-semibold text-pl-text-primary" {...props} />
  ),
  h2: ({ node, ...props }) => (
    <h2 className="mb-0 mt-8 first:mt-0 border-b border-pl-border pb-0 text-18 font-display font-semibold text-pl-text-primary" {...props} />
  ),
  h3: ({ node, ...props }) => (
    <h3 className="mt-8 mb-2 text-16 font-semibold text-pl-text-primary" {...props} />
  ),
  p: ({ node, ...props }) => (
    <p className="mt-4 leading-8 text-pl-text-secondary" {...props} />
  ),
  strong: ({ node, ...props }) => (
    <strong className="font-semibold text-pl-text-secondary" {...props} />
  ),
  ul: ({ node, ...props }) => (
    <ul className="mt-4 list-disc pl-6 text-pl-text-secondary" {...props} />
  ),
  ol: ({ node, ...props }) => (
    <ol className="mt-4 list-decimal pl-6 text-pl-text-secondary" {...props} />
  ),
  li: ({ node, ...props }) => <li className="mt-2" {...props} />,
  blockquote: ({ node, ...props }) => (
    <blockquote
      className="mt-6 border-l-4 border-pl-accent/50 pl-4 italic text-pl-text-secondary"
      {...props}
    />
  ),
  a: ({ node, ...props }) => (
    <a
      className="text-pl-accent underline underline-offset-2"
      target="_blank"
      rel="noreferrer"
      {...props}
    />
  ),
  code: ({ inline, node, className, children, ...props }) =>
    inline ? (
      <code className="rounded bg-pl-bg-subtle px-1 py-0.5 text-sm" {...props}>
        {children}
      </code>
    ) : (
      <code
        className="block overflow-x-auto rounded-lg bg-pl-bg-subtle p-4 text-sm"
        {...props}
      >
        {children}
      </code>
    ),
};

function decodeCaseNumberFromParam(value) {
  if (!value) return "";
  return decodeCaseNumberFromPath(value);
}

function formatDate(date) {
  const parsed = new Date(date);

  if (Number.isNaN(parsed.getTime())) {
    return "";
  }

  return parsed.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

const CaseSummary = () => {
  const navigate = useNavigate();
  const { state, signIn, getAccessToken } = useAuthContext();
  const params = useParams();
  const routeCaseNumber = decodeCaseNumberFromParam(params["*"]);
  const [caseDocument, setCaseDocument] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [pdfLoading, setPdfLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const loadCase = async () => {
      setIsLoading(true);
      setLoadError("");

      try {
        if (state.isLoading) return;
        if (!state.isAuthenticated) {
          setCaseDocument(null);
          setLoadError("Please sign in to view this case summary.");
          return;
        }
        const accessToken = await getAccessToken();
        const result = await getCaseByStandardCaseNumber(routeCaseNumber, accessToken);

        if (cancelled) return;

        if (!result) {
          setCaseDocument(null);
          setLoadError("No judgment was found for this case number.");
          return;
        }

        setCaseDocument(result);
      } catch (error) {
        if (cancelled) return;
        console.error("Failed to load case summary:", error);
        setCaseDocument(null);
        setLoadError("The case summary could not be loaded right now.");
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    loadCase();

    return () => {
      cancelled = true;
    };
  }, [getAccessToken, routeCaseNumber, state.isAuthenticated, state.isLoading]);

  const caseNumbers = useMemo(
    () => normalizeCaseNumbers(caseDocument?.standard_casenumber),
    [caseDocument]
  );
  const primaryCaseNumber = caseNumbers[0] || routeCaseNumber;
  const caseNumberLabel = caseNumbers.join(", ") || routeCaseNumber;
  const canonicalPath = primaryCaseNumber
    ? `/summary/${encodeCaseNumberForPath(primaryCaseNumber)}`
    : "/summary";

  const handleBackToResults = () => {
    const storedBackPath = sessionStorage.getItem("summaryBackPath");

    if (storedBackPath) {
      try {
        const { summaryPath, backPath } = JSON.parse(storedBackPath);
        const currentPath = window.location.pathname;

        if (backPath && (summaryPath === canonicalPath || summaryPath === currentPath)) {
          sessionStorage.removeItem("summaryBackPath");
          navigate(backPath);
          return;
        }

        sessionStorage.removeItem("summaryBackPath");
      } catch {
        sessionStorage.removeItem("summaryBackPath");
      }
    }

    navigate(-1);
  };

  useEffect(() => {
    if (!caseDocument || !primaryCaseNumber) return;

    if (primaryCaseNumber !== routeCaseNumber) {
      navigate(canonicalPath, { replace: true });
    }
  }, [canonicalPath, caseDocument, navigate, primaryCaseNumber, routeCaseNumber]);

  const openPDFWithPreSignedUrl = async (originalLink) => {
    setPdfLoading(true);

    const newWindow = window.open("about:blank", "_blank");

    try {
      let accessToken = "";
      try {
        accessToken = (await getAccessToken()) || "";
      } catch (tokenError) {
        console.error("Failed to get access token for PDF:", tokenError);
      }
      const response = await fetch(`${PRESIGNED_BASE_URL}/api/pdf/get-pdf-url`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
        },
        body: JSON.stringify({
          link: originalLink,
        }),
      });

      const data = await response.json();

      if (data.success && newWindow) {
        newWindow.location.href = data.presigned_url;
        return;
      }

      if (newWindow) {
        newWindow.location.href = originalLink;
      }
    } catch (error) {
      console.error("Error fetching pre-signed URL:", error);
      if (newWindow) {
        newWindow.location.href = originalLink;
      }
    } finally {
      setPdfLoading(false);
    }
  };

  const handleViewJudgment = async () => {
    if (!caseDocument?.link || pdfLoading) return;

    if (!state.isAuthenticated) {
      sessionStorage.setItem("redirectToDocument", caseDocument.link);
      sessionStorage.setItem("redirectAfterLogin", canonicalPath);
      signIn();
      return;
    }

    await openPDFWithPreSignedUrl(caseDocument.link);
  };

  useEffect(() => {
    if (!state.isAuthenticated) return;

    const redirectToDocument = sessionStorage.getItem("redirectToDocument");
    const redirectAfterLogin = sessionStorage.getItem("redirectAfterLogin");

    if (!redirectToDocument && redirectAfterLogin === canonicalPath) {
      sessionStorage.removeItem("redirectAfterLogin");
      return;
    }

    if (!redirectToDocument || redirectAfterLogin !== canonicalPath) {
      return;
    }

    openPDFWithPreSignedUrl(redirectToDocument);
    sessionStorage.removeItem("redirectToDocument");
    sessionStorage.removeItem("redirectAfterLogin");
  }, [canonicalPath, state.isAuthenticated]);

  const rawSummary = caseDocument?.summary_text?.trim() || "";
  // Extract metadata block and body separately
  const metadataMatch = rawSummary.match(/## Metadata\s*\n([\s\S]*?)(?=\n## )/i);
  const metadataLines = metadataMatch
    ? metadataMatch[1].split("\n").filter((l) => l.trim().startsWith("**"))
    : [];
  const metadataRows = metadataLines
    .map((line) => line.match(/\*\*(.+?):\*\*\s*(.*)/))
    .filter(Boolean)
    .map((match) => ({ label: match[1], value: match[2] }))
    .filter(({ label }) => !["case number", "parties", "judgment date"].includes(label.trim().toLowerCase()));
  const summaryText = rawSummary.replace(/## Metadata[\s\S]*?(?=## )/i, "");
  const parties = caseDocument?.nameofparties?.trim() || "";
  const judges = Array.isArray(caseDocument?.judge_final)
    ? caseDocument.judge_final.filter((judge) => judge && judge !== "NA")
    : [];

  return (
    <div className="relative">
      <Helmet>
        <title>
          {primaryCaseNumber
            ? `${primaryCaseNumber} | Case Summary | paralegal.lk`
            : "Case Summary | paralegal.lk"}
        </title>
        <meta
          name="description"
          content={
            summaryText.slice(0, 160) ||
            `Summary and judgment access for ${primaryCaseNumber || "this case"}.`
          }
        />
        <meta name="robots" content="noindex,nofollow" />
        <link rel="canonical" href={`https://www.paralegal.lk${canonicalPath}`} />
      </Helmet>

      <SearchLayout
        isResults={true}
        showTab={false}
        showSearch={false}
      >
        <section className="flex w-full max-w-6xl flex-col px-6 pb-20 pt-[120px] md:px-20 md:pt-[120px]">
          <button
            onClick={handleBackToResults}
            className="mb-4 flex items-center gap-1 text-14 text-pl-text-tertiary hover:text-pl-accent transition-colors"
          >
            <span>&larr;</span> Back to results
          </button>
          {isLoading ? (
            <div className="rounded-2xl border border-pl-border bg-pl-bg-elevated p-8">
              <p className="text-16 text-pl-text-secondary">
                Loading case summary...
              </p>
            </div>
          ) : loadError ? (
            <div className="rounded-2xl border border-pl-border bg-pl-bg-elevated p-8">
              <h1 className="text-2xl font-display font-semibold text-pl-text-primary">
                Case not found
              </h1>
              <p className="mt-3 text-pl-text-secondary">{loadError}</p>
            </div>
          ) : (
            <article className="rounded-pl-lg border border-pl-border bg-pl-bg-elevated p-[20px_24px]">
              {/* Header */}
              <div className="flex flex-col gap-3 border-b border-pl-border pb-5 md:flex-row md:items-center md:justify-between">
                <div className="flex flex-col gap-2">
                  <div className="flex items-baseline gap-3">
                    <h1 className="font-mono text-xl font-medium text-pl-text-primary md:text-2xl">
                      {caseNumberLabel}
                    </h1>
                    {caseDocument?.date && (
                      <span className="text-13 text-pl-text-tertiary">
                        {formatDate(caseDocument.date)}
                      </span>
                    )}
                  </div>
                  {parties && (
                    <p className="text-18 font-medium text-pl-text-primary">
                      {parties}
                    </p>
                  )}
                </div>
                <button
                  type="button"
                  onClick={handleViewJudgment}
                  disabled={!caseDocument?.link || pdfLoading}
                  className="flex-none rounded-pl border border-pl-accent px-4 py-1.5 text-14 font-medium leading-5 text-pl-accent transition-colors duration-150 hover:bg-pl-accent hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {pdfLoading ? "Opening..." : "View judgment"}
                </button>
              </div>

              {/* Metadata */}
              {metadataRows.length > 0 && (
                <dl className="grid gap-x-6 gap-y-1 py-4 text-14 md:grid-cols-[auto_1fr]">
                  {metadataRows.map(({ label, value }) => (
                    <React.Fragment key={label}>
                      <dt className="text-pl-text-tertiary">{label}</dt>
                      <dd className="text-pl-text-secondary mb-1 md:mb-0">{value}</dd>
                    </React.Fragment>
                  ))}
                </dl>
              )}

              {/* Body */}
              <div className="pt-0">
                {summaryText ? (
                  <ReactMarkdown
                    components={markdownComponents}
                    remarkPlugins={[remarkGfm, remarkBreaks]}
                  >
                    {summaryText}
                  </ReactMarkdown>
                ) : (
                  <p className="text-15 leading-7 text-pl-text-secondary">
                    No summary is available for this judgment yet.
                  </p>
                )}
              </div>
            </article>
          )}
        </section>
      </SearchLayout>
    </div>
  );
};

export default CaseSummary;
